import React, {useEffect, useRef, useState} from "react"
import BottomButtons from "../../../components/button-groups/bottom-buttons/BottomButtons";
import {useNavigate, useParams} from "react-router-dom";
import {PATH_SCREEN_STRING, PATH_STRINGS} from "../../../routes/route-props";
import "./styles.css"
import ChooseFlight from "../../../components/activity/flight/choose-flight/ChooseFlight";
import {useAppDispatch, useAppSelector} from "../../../redux/reducer/store";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isActivityWithReturnRouteProps
} from "../../../utils/check/type-guards";
import {
    addActivityToTravelComponents,
    deleteActivityFromTravelComponents,
    updateActivityInTravelComponents
} from "../../../redux/reducer/travel-components";
import {v4 as uuidv4} from "uuid";
import {toast} from "react-toastify";
import ToastWithTextOnly from "../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {useTranslation} from "react-i18next";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../../types-enums/activity";
import ChooseHotel, {defaultHotelStayDateRange} from "../../../components/activity/hotel/choose-hotel/ChooseHotel";
import {addActivityToJourney, DEFAULT_JOURNEY, updateActivityInJourney} from "../../../redux/reducer/journey";
import {generateJourneyDayCountToolTip, generateTravelComponentCountToolTip} from "../../../utils/string/tooltips";
import {getFirstActivityByActivityId} from "../../../utils/getter/activities";
import {EDIT_ACTIVITY} from "../../../types-enums";
import {hasExactDates} from "../../../utils/getter/journey";
import {DateTime} from "luxon";
import cloneDeep from "lodash/cloneDeep";
import {isEqual} from "lodash";
import {DurationProps} from "../../../types-enums/activity-common-properties";
import {compareCarBusProps, compareHotelProps} from "../../../utils/converter/travel-components";
import {activityHasReturnOption, changeDateOnDayChange, groupActivities} from "../../../utils/check/activities";
import ChooseCarBus from "../../../components/activity/car-bus/ChooseCarBus";
import {generateTranslatedActivityTypeString} from "../../../utils/string/common";
import {
    JOURNEY_DAY_CARD_SCROLL_TO_ID
} from "../../../components/journey/journey-overview/journey-day-card/JourneyDayCard";
import {getStartAndEndDurationOfHotelStay} from "../../../utils/getter/hotel";
import {getStateOfReturnActivity, RETURN_ACTIVITY_UPDATE_STATE} from "./utils";
import {getDistanceKmOfCarBusGroup} from "../../../utils/getter/car-bus";

const getActivityForEdit = (
    id: string | undefined,
    screen: string | undefined,
    travelComponentsActivities: AllActivityTypesArray,
    journeyActivities: AllActivityTypesArray,
    editInBulk: boolean,
    day: number | null,
    journeyDuration: DurationProps
) => {
    let activityForFormToEdit: AllActivityTypes | null = null
    if (typeof id !== 'undefined' && typeof screen !== 'undefined') {
        const screenEnumValue = PATH_SCREEN_STRING[screen as keyof typeof PATH_SCREEN_STRING]
        switch (screenEnumValue) {
            case PATH_SCREEN_STRING.tc:
                activityForFormToEdit = getFirstActivityByActivityId(travelComponentsActivities, id)
                break
            case PATH_SCREEN_STRING.journey:
                activityForFormToEdit = getFirstActivityByActivityId(journeyActivities, id)
                if (activityForFormToEdit !== null) {
                    const {common: {activityType}} = activityForFormToEdit
                    if (changeDateOnDayChange(activityType)) {
                        if (isActivityHotelState(activityForFormToEdit) && day !== null) {
                            if (!editInBulk) {
                                const updatedActivity = cloneDeep(activityForFormToEdit)
                                const {hotelStayDuration} = updatedActivity
                                updatedActivity.common.groupId = null
                                updatedActivity.common.day = day
                                if (hasExactDates(hotelStayDuration) && hasExactDates(journeyDuration)) {
                                    const {
                                        startDate: journeyStartDate,
                                        endDate: journeyEndDate
                                    } = journeyDuration.dates!!
                                    updatedActivity.hotelStayDuration.dates = {
                                        startDate: journeyStartDate.plus({days: day - 1}),
                                        endDate: journeyStartDate.plus({days: day}),
                                    }
                                } else {
                                    updatedActivity.hotelStayDuration.daysOrNights = 1
                                }
                                activityForFormToEdit = updatedActivity
                            }
                        }
                    }
                }
                break
            default:
                screenEnumValue satisfies never
        }
    }
    return activityForFormToEdit
}

const getActivitiesAccordingToScreen = (
    screen: string | undefined,
    travelComponentsActivities: AllActivityTypesArray,
    journeyActivities: AllActivityTypesArray,
    screenEnumValue: PATH_SCREEN_STRING
) => {

    if (typeof screen !== 'undefined') {
        switch (screenEnumValue) {
            case PATH_SCREEN_STRING.tc:
                return travelComponentsActivities
                break
            case PATH_SCREEN_STRING.journey:
                return journeyActivities
                break
            default:
                screenEnumValue satisfies never
        }
    }
    return []
}

const ActivityForm = () => {
    const {activities: travelComponentsActivities} = useAppSelector(state => state.travelComponents)
    const {
        bundleId: journeyBundleId,
        activities: journeyActivities,
        name: journeyName,
        description: journeyDescription,
        customer: journeyCustomer,
        journeyDuration: journeyDurationFromStore,
        personCount: journeyPersonCount
    } = useAppSelector(state => state.journey)
    const {journeyDuration} = useAppSelector(state => state.journey)
    // [AllActivityTypes, AllActivityTypes] is one-way- and return Activity
    const [calculatedActivity, setCalculatedActivity] = useState<AllActivityTypes | [AllActivityTypes, AllActivityTypes] | null>(null)
    const [resetActivityForm, setResetActivityForm] = useState(0)
    const activityCalculated = !Boolean(calculatedActivity)
    const {activityType, id, day, screen, bulkOrSingle} = useParams()
    const editInBulk = bulkOrSingle === EDIT_ACTIVITY.bulk
    const dayFormatted = typeof day !== 'undefined' ? parseInt(day, 10) : null
    const [activityToEdit, setActivityToEdit] = useState<AllActivityTypes | null>(getActivityForEdit(
        id,
        screen,
        travelComponentsActivities,
        journeyActivities,
        editInBulk,
        dayFormatted,
        journeyDuration
    ))
    const isEdit = typeof id === "string"
    const {t, i18n} = useTranslation()
    const activityTypeString = useRef(generateTranslatedActivityTypeString(ACTIVITY_TYPE[activityType as keyof typeof ACTIVITY_TYPE], i18n))
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const journeyWithExactDates = hasExactDates(journeyDuration)
    const screenEnumValue = PATH_SCREEN_STRING[screen as keyof typeof PATH_SCREEN_STRING]
    const activitiesForScreen = getActivitiesAccordingToScreen(screen, travelComponentsActivities, journeyActivities, screenEnumValue)
    const originalActivity = useRef<AllActivityTypes | null>(null)

    useEffect(() => {
        switch (screenEnumValue) {
            case PATH_SCREEN_STRING.journey:
                const emptyJourney = isEqual({
                    bundleId: journeyBundleId,
                    name: journeyName,
                    description: journeyDescription,
                    activities: journeyActivities,
                    customer: journeyCustomer,
                    journeyDuration: journeyDurationFromStore,
                    personCount: journeyPersonCount,
                }, DEFAULT_JOURNEY)
                if (emptyJourney) navigate(PATH_STRINGS.travelDatabase)
                break;
            case PATH_SCREEN_STRING.tc:
                if (
                    id &&
                    !(travelComponentsActivities.findIndex(activity => activity.common.id === id) > -1)
                ) navigate(PATH_STRINGS.travelDatabase)
                break
            default:
                screenEnumValue satisfies never;
        }

    }, []);

    useEffect(() => {
        const activity = getActivityForEdit(
            id,
            screen,
            travelComponentsActivities,
            journeyActivities,
            editInBulk,
            dayFormatted,
            journeyDuration
        )
        originalActivity.current = activity
        setActivityToEdit(activity)
    }, [id, screen, travelComponentsActivities, journeyActivities])

    /**
     * When a Bundle is loaded, the Activities in the bundle might be updated. Assuming a persisted Bundle is
     * one year old and contains hotels, a hotel could have by now a different name. This is feedbacked to the user
     * through a tooltip connected to the Activity. If the user edits the loaded Activity and changes the data that
     * causes the hint to show, the hint must be removed. This is what this function does.
     */
    const _removeHintsFromLoadingBundle = (activity: AllActivityTypes) => {
        const {common: {activityType}} = activity
        const updatedActivity = cloneDeep(activity)
        switch (activityType) {
            case ACTIVITY_TYPE.flight:
                if (isActivityFlightState(updatedActivity) && isActivityFlightState(originalActivity.current)) {
                    const {legs: originalLegs} = originalActivity.current
                    if (updatedActivity.legs.length !== originalLegs.length) {
                        updatedActivity.legs.forEach(updatedLeg => {
                            updatedLeg.informationFromLoadedBundle = null
                        })
                    } else {
                        updatedActivity.legs.forEach((updatedLeg, index) => {
                            if (!isEqual(updatedLeg, originalLegs[index])) updatedLeg.informationFromLoadedBundle = null
                        })
                    }
                }
                break;
            case ACTIVITY_TYPE.hotel:
                if (isActivityHotelState(updatedActivity) && isActivityHotelState(originalActivity.current)) {
                    if (!isEqual(updatedActivity, originalActivity.current)) updatedActivity.informationFromLoadedBundle = null
                }
                break;
            case ACTIVITY_TYPE.car_bus:
                if (isActivityCarBusState(updatedActivity) && (isActivityCarBusState(originalActivity.current))) {
                    if (!isEqual(updatedActivity, originalActivity.current)) updatedActivity.informationFromLoadedBundle = null
                }
                break;
            default:
                activityType satisfies never;
        }

        return updatedActivity
    }

    const _persistActivity = () => {
        if (calculatedActivity) {
            const screenEnumValue = PATH_SCREEN_STRING[screen as keyof typeof PATH_SCREEN_STRING]
            const updatedCalculatedActivities: AllActivityTypesArray = []
            if (Array.isArray(calculatedActivity)) {
                calculatedActivity.forEach(activity => updatedCalculatedActivities.push(_removeHintsFromLoadingBundle(activity)))
            } else {
                updatedCalculatedActivities.push(_removeHintsFromLoadingBundle(calculatedActivity))
            }

            switch (screenEnumValue) {
                case (PATH_SCREEN_STRING.journey):
                    // in journeys, one cannot edit return Activities, because there are no return Activities
                    if (dayFormatted !== null) {
                        if (isEdit && typeof id === "string") {
                            updatedCalculatedActivities.forEach(updatedCalculatedActivity => {
                                const {common: {activityType}} = updatedCalculatedActivity
                                if (groupActivities(activityType)) {
                                    if (
                                        isActivityHotelState(originalActivity.current) &&
                                        isActivityHotelState(updatedCalculatedActivity) &&
                                        compareHotelProps(originalActivity.current, updatedCalculatedActivity)
                                    ) updatedCalculatedActivity.common.semanticId = uuidv4()

                                    if (
                                        isActivityCarBusState(originalActivity.current) &&
                                        isActivityCarBusState(updatedCalculatedActivity) &&
                                        compareCarBusProps(originalActivity.current, updatedCalculatedActivity)
                                    ) updatedCalculatedActivity.common.semanticId = uuidv4()
                                }
                                dispatch(updateActivityInJourney({
                                    activity: updatedCalculatedActivity,
                                    travelComponentActivities: travelComponentsActivities,
                                    day: dayFormatted,
                                }))
                            })
                        } else {
                            updatedCalculatedActivities.forEach(updatedCalculatedActivity => {
                                dispatch(addActivityToJourney({
                                    activity: updatedCalculatedActivity,
                                    travelComponentActivities: travelComponentsActivities,
                                    day: dayFormatted,
                                }))
                            })
                        }
                    }
                    break;
                case (PATH_SCREEN_STRING.tc):
                    // for Activities that can have a return, the following options must be checked
                    // depending on the case, different Actions for the store must be dispatched
                    // original has return id && new has no return id -> update and remove
                    // origin has no return id && new has return id -> update and add
                    // original has return id && new has return id -> update both
                    let returnActivity: AllActivityTypes | null = null
                    const updatedCalculatedActivity = updatedCalculatedActivities[0]
                    if (updatedCalculatedActivities.length === 2) returnActivity = updatedCalculatedActivities[1]
                    if (typeof id !== 'undefined') {
                        if (isActivityWithReturnRouteProps(updatedCalculatedActivity)) {
                            if (
                                getStateOfReturnActivity(
                                    updatedCalculatedActivity,
                                    travelComponentsActivities,
                                    RETURN_ACTIVITY_UPDATE_STATE.ORIGINAL_HAS_RETURN_AND_UPDATED_ACTIVITY_HAS_RETURN,
                                    id
                                )
                            ) {
                                dispatch(updateActivityInTravelComponents({
                                    activity: updatedCalculatedActivity,
                                    id
                                }))
                                if (returnActivity) dispatch(updateActivityInTravelComponents({
                                    activity: returnActivity,
                                    id: returnActivity.common.id
                                }))
                            } else if (
                                getStateOfReturnActivity(
                                    updatedCalculatedActivity,
                                    travelComponentsActivities,
                                    RETURN_ACTIVITY_UPDATE_STATE.ORIGINAL_HAS_RETURN_AND_UPDATED_ACTIVITY_HAS_NO_RETURN,
                                    id
                                ) &&
                                activityHasReturnOption(updatedCalculatedActivity)
                            ) {
                                const activityIndexToUpdate = travelComponentsActivities.map(activity => activity.common.id).indexOf(id)
                                const originalActivity = travelComponentsActivities[activityIndexToUpdate]
                                if (activityIndexToUpdate > -1) {
                                    if (activityHasReturnOption(originalActivity)) {
                                        const activityIndexToDelete = travelComponentsActivities.map(activity => activity.common.id).indexOf(originalActivity.returnId!!)
                                        if (activityIndexToDelete > -1) dispatch(deleteActivityFromTravelComponents({
                                            id: travelComponentsActivities[activityIndexToDelete].common.id,
                                            onlyDeleteReturnActivity: true
                                        }))
                                    }
                                }
                                dispatch(updateActivityInTravelComponents({
                                    activity: updatedCalculatedActivity,
                                    id
                                }))
                            } else if (
                                getStateOfReturnActivity(
                                    updatedCalculatedActivity,
                                    travelComponentsActivities,
                                    RETURN_ACTIVITY_UPDATE_STATE.ORIGINAL_HAS_NO_RETURN_AND_UPDATED_ACTIVITY_HAS_RETURN,
                                    id
                                )
                            ) {
                                dispatch(updateActivityInTravelComponents({
                                    activity: updatedCalculatedActivity,
                                    id
                                }))
                                if (returnActivity) {
                                    const {common: {activityType}} = returnActivity
                                    switch (activityType) {
                                        case ACTIVITY_TYPE.hotel:
                                            break;
                                        case ACTIVITY_TYPE.car_bus:
                                            break;
                                        case ACTIVITY_TYPE.flight:
                                            // the flight form simply dupicates the one-way legs and reverses them.
                                            // this also means, that the backend IDs of the one-way-legs are assigned to the return legs
                                            // we don't want that!
                                            if (isActivityFlightState(returnActivity)) returnActivity.legs.forEach(leg => leg.backendId = null)
                                    }
                                    dispatch(addActivityToTravelComponents(returnActivity))
                                }
                            } else if (
                                getStateOfReturnActivity(
                                    updatedCalculatedActivity,
                                    travelComponentsActivities,
                                    RETURN_ACTIVITY_UPDATE_STATE.ORIGINAL_HAS_NO_RETURN_AND_UPDATED_ACTIVITY_HAS_NO_RETURN,
                                    id
                                )
                            ) {
                                dispatch(updateActivityInTravelComponents({
                                    activity: updatedCalculatedActivity,
                                    id
                                }))
                                if (returnActivity) dispatch(updateActivityInTravelComponents({
                                    activity: returnActivity,
                                    id: returnActivity.common.id
                                }))
                            } else {
                                //
                            }
                        } else {
                            dispatch(updateActivityInTravelComponents({
                                activity: updatedCalculatedActivity,
                                id
                            }))
                        }
                    } else {
                        dispatch(addActivityToTravelComponents(updatedCalculatedActivity))
                        if (returnActivity) dispatch(addActivityToTravelComponents(returnActivity))
                    }
                    break
                default:
                    return screenEnumValue satisfies never;
            }

        }
    }

    const _showToastWhenActivityAdded = () => toast(<ToastWithTextOnly
            message={t(`TOASTS.SUCCESS.ACTIVITY_ADDED_TO_${screen === PATH_SCREEN_STRING.journey ? 'JOURNEY' : 'TRAVEL_COMPONENTS'}`,
                {
                    activity: activityTypeString.current,
                    interpolation: {escapeValue: false}
                })
            }/>,
        {progress: undefined, type: "success"}
    )

    const _renderActivityForm = (activityType: ACTIVITY_TYPE) => {
        const hotelStayDateRange = defaultHotelStayDateRange
        let calendarMinDate: DateTime | undefined = undefined
        let calendarMaxDate: DateTime | undefined = undefined
        let openToDate: DateTime | undefined = undefined
        let disableDate = true
        let maxPersons: number | null = null
        const updatedActivityToEdit = cloneDeep(activityToEdit)
        switch (screenEnumValue) {
            case PATH_SCREEN_STRING.tc:
                disableDate = false
                if (isActivityHotelState(updatedActivityToEdit)) {
                    const {hotelStayDuration} = updatedActivityToEdit
                    if (hasExactDates(hotelStayDuration)) {
                        const {startDate: hotelStartDate, endDate: hotelEndDate} = hotelStayDuration.dates!!
                        hotelStayDateRange.startDate = hotelStartDate
                        hotelStayDateRange.endDate = hotelEndDate
                    }
                }
                break
            case PATH_SCREEN_STRING.journey:
                if (journeyWithExactDates && dayFormatted !== null && typeof dayFormatted !== 'undefined') {
                    disableDate = false
                    const {dates} = journeyDuration
                    const {startDate: journeyStartDate, endDate: journeyEndDate} = dates!!
                    openToDate = journeyStartDate.plus({days: dayFormatted - 1})
                    calendarMinDate = openToDate
                    calendarMaxDate = journeyEndDate
                    // Hotels can be grouped and because of that, if a hotel has a duration of several days,
                    // in the redux store, this hotel is split in n single Activities (according to duration).
                    // Because of that, each single hotel has only a duration of one night (either expressed through date
                    // or daysOrNights). Because of that, we must re-calculate the actual duration of the hotel,
                    // in order to display it to the user
                    if (isActivityHotelState(updatedActivityToEdit)) {
                        const {hotelStayDuration} = updatedActivityToEdit
                        const {
                            minValue: hotelStart,
                            maxValue: hotelEnd
                        } = getStartAndEndDurationOfHotelStay(journeyActivities.filter(isActivityHotelState), updatedActivityToEdit)
                        if (hasExactDates(hotelStayDuration)) {
                            if (DateTime.isDateTime(hotelStart) && DateTime.isDateTime(hotelEnd)) {
                                if (editInBulk) {
                                    hotelStayDateRange.startDate = hotelStart
                                    hotelStayDateRange.endDate = hotelEnd
                                } else {
                                    hotelStayDateRange.startDate = hotelStart
                                    hotelStayDateRange.endDate = hotelStart.plus({days: 1})
                                }
                                updatedActivityToEdit.hotelStayDuration.dates = hotelStayDateRange
                                updatedActivityToEdit.hotelStayDuration.daysOrNights = null
                            }
                        } else {
                            updatedActivityToEdit.hotelStayDuration.dates = null
                            if (typeof hotelEnd === 'number') updatedActivityToEdit.hotelStayDuration.daysOrNights = hotelEnd
                        }
                    }
                } else {
                    openToDate = DateTime.now()
                    if (isActivityHotelState(updatedActivityToEdit)) {
                        updatedActivityToEdit.hotelStayDuration.dates = null
                        const {maxValue: hotelEnd} = getStartAndEndDurationOfHotelStay(
                            journeyActivities.filter(isActivityHotelState),
                            updatedActivityToEdit
                        )
                        if (typeof hotelEnd === 'number') updatedActivityToEdit.hotelStayDuration.daysOrNights = hotelEnd
                    }
                }
                maxPersons = journeyPersonCount
                break
            default:
                screenEnumValue satisfies never
        }

        if (isActivityCarBusState(updatedActivityToEdit) && isActivityCarBusState(activityToEdit)) {
            if (editInBulk) {
                if (activityToEdit.originalCountDays !== null) updatedActivityToEdit.countDays = activityToEdit.originalCountDays
                const distanceKmOfAllCarBus = getDistanceKmOfCarBusGroup(updatedActivityToEdit, journeyActivities.filter(isActivityCarBusState))
                if (updatedActivityToEdit.manualEnteredDistanceKm !== null) {
                    updatedActivityToEdit.manualEnteredDistanceKm = distanceKmOfAllCarBus
                } else {
                    if (updatedActivityToEdit.calculatedDistanceKm !== null) updatedActivityToEdit.calculatedDistanceKm = distanceKmOfAllCarBus
                }
            }
        }

        switch (activityType) {
            case ACTIVITY_TYPE.flight:
                return <ChooseFlight
                    fromWhichScreen={screenEnumValue}
                    disableDate={disableDate}
                    day={dayFormatted}
                    resetForm={() => {
                        setCalculatedActivity(null)
                        setResetActivityForm(resetActivityForm + 1)
                    }}
                    key={resetActivityForm}
                    isEdit={isEdit}
                    openToDate={openToDate}
                    minDate={calendarMinDate}
                    maxDate={calendarMaxDate}
                    flightToEdit={isActivityFlightState(updatedActivityToEdit) ? updatedActivityToEdit : null}
                    getCalculatedActivity={setCalculatedActivity}
                    maxPersons={maxPersons}
                />
            case ACTIVITY_TYPE.hotel:
                return <ChooseHotel
                    whichScreenEditFrom={screenEnumValue}
                    disableDateRangePicker={disableDate}
                    useGroupId={bulkOrSingle === EDIT_ACTIVITY.bulk}
                    activities={activitiesForScreen}
                    openToDate={openToDate}
                    minDate={calendarMinDate}
                    maxDate={calendarMaxDate?.minus({days: 1})}
                    journeyDuration={journeyDuration}
                    day={dayFormatted}
                    resetForm={() => {
                        setCalculatedActivity(null)
                        setResetActivityForm(resetActivityForm + 1)
                    }}
                    key={resetActivityForm}
                    isEdit={isEdit}
                    hotelToEdit={isActivityHotelState(updatedActivityToEdit) ? updatedActivityToEdit : null}
                    getCalculatedActivity={setCalculatedActivity}
                    maxPersons={maxPersons}
                />
            case ACTIVITY_TYPE.car_bus:
                return <ChooseCarBus
                    key={resetActivityForm}
                    resetForm={() => {
                        setCalculatedActivity(null)
                        setResetActivityForm(resetActivityForm + 1)
                    }}
                    carBusToEdit={isActivityCarBusState(updatedActivityToEdit) ? updatedActivityToEdit : null}
                    getCalculatedActivity={setCalculatedActivity}
                    day={dayFormatted}
                    journeyDuration={journeyDuration}
                    maxPersons={maxPersons}
                    isEdit={isEdit}/>
            default:
                return activityType satisfies never
        }
    }

    const _setButtonText = (button: 'SECOND' | 'THIRD' | "FOURTH") => {
        let appendix = ''
        let type = ''

        if (typeof id === "undefined") {
            appendix = "ADD"
        } else {
            appendix = "UPDATE"
        }

        if (screen === PATH_SCREEN_STRING.journey) {
            type = t('GENERAL.JOURNEY')
        } else {
            type = t('GENERAL.TRAVEL_COMPONENTS_DATIV')
        }

        if (button === 'FOURTH') {
            return t(`SCREENS.COMMON.BUTTON_BOTTOM_ACTIVITY.${button}_BUTTON_${appendix}`, {
                interpolation: {escapeValue: false},
                type,
            })
        }

        return t(`SCREENS.COMMON.BUTTON_BOTTOM_ACTIVITY.${button}_BUTTON_${appendix}`, {
            interpolation: {escapeValue: false},
            type
        })
    }

    const _constructToolTipForLastButton = () => {
        let tooltip = ''
        const screenEnumValue = PATH_SCREEN_STRING[screen as keyof typeof PATH_SCREEN_STRING]
        switch (screenEnumValue) {
            case PATH_SCREEN_STRING.tc:
                tooltip = generateTravelComponentCountToolTip(travelComponentsActivities, i18n)
                break;
            case PATH_SCREEN_STRING.journey:
                tooltip = generateJourneyDayCountToolTip(journeyDuration, journeyActivities, i18n)
                break;
            default:
                return screenEnumValue satisfies never
        }
        return tooltip
    }

    return <>
        <div className={"activity-form-calculate-container"}>
            {_renderActivityForm(activityType as ACTIVITY_TYPE)}
        </div>
        <BottomButtons
            buttons={[
                {
                    text: "GENERAL.ABORT",
                    variant: "abort",
                    onClick: () => navigate(-1)
                },
                {
                    text: _setButtonText("SECOND"),
                    onClick: () => {
                        _persistActivity()
                        _showToastWhenActivityAdded()
                        navigate(PATH_STRINGS.selectTravelComponents)
                    },
                    disabled: activityCalculated
                },
                {
                    text: _setButtonText('THIRD'),
                    onClick: () => {
                        _persistActivity()
                        if (screen === PATH_SCREEN_STRING.tc) {
                            navigate(PATH_STRINGS.travelComponentsOverview)
                        } else {
                            if (typeof day === "undefined") {
                                navigate(PATH_STRINGS.journey)
                            } else {
                                navigate(PATH_STRINGS.journey, {state: {scrollToDay: `${JOURNEY_DAY_CARD_SCROLL_TO_ID}${day}`}})
                            }
                        }
                    },
                    disabled: activityCalculated
                },
                {
                    text: _setButtonText("FOURTH"),
                    onClick: () => {
                        _persistActivity()
                        _showToastWhenActivityAdded()
                        setResetActivityForm(resetActivityForm + 1)
                        setCalculatedActivity(null)
                        setActivityToEdit(null)
                        originalActivity.current = null
                    },
                    disabled: activityCalculated
                },
                {
                    tooltip: _constructToolTipForLastButton(),
                    text: screen === PATH_SCREEN_STRING.journey ? "SCREENS.COMMON.GO_TO_JOURNEY" : "SCREENS.COMMON.GO_TO_TRAVEL_COMPONENTS",
                    onClick: () => screen === PATH_SCREEN_STRING.journey ? navigate(PATH_STRINGS.journey) : navigate(PATH_STRINGS.travelComponentsOverview),
                },
            ]}
        />
    </>
}

export default ActivityForm